import React from 'react';
import { MapPin, Star, BadgeCheck, HelpCircle, Eye, Copy, MessageCircle } from 'lucide-react';
import { isVenueClaimed } from './venueHelpers';

const VenueInfoCard = React.memo(({ venue, onShare }) => {
  return (
    <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
      <div className="p-6 sm:p-8">
        {/* Header with Badge */}
        <div className="mb-6">
          <div className="flex items-start gap-1.5 sm:gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
                {venue.name}
              </h1>
              {(venue.currentPlan?.toLowerCase() === 'pro' || venue.currentPlan?.toLowerCase() === 'premium') && (
                <BadgeCheck className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 fill-blue-100 flex-shrink-0" title="Doğrulanmış Mekan" />
              )}
              {!isVenueClaimed(venue) && (
                <div className="relative inline-block group">
                  <button 
                    className="flex-shrink-0 text-amber-500 hover:text-amber-600 active:text-amber-700 transition-colors touch-manipulation peer"
                    aria-label="Mekan bilgisi"
                  >
                    <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <div className="absolute right-0 sm:right-auto sm:left-0 top-full mt-2 w-[calc(100vw-3rem)] sm:w-80 max-w-xs p-3 bg-amber-50 border border-amber-200 rounded-xl shadow-lg text-amber-800 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 peer-focus:opacity-100 peer-focus:visible peer-active:opacity-100 peer-active:visible">
                    <p className="text-xs sm:text-sm leading-relaxed">
                      Bu mekan bilgileri Google Places API&apos;den otomatik olarak alınmıştır. 
                      Mekan sahibi iseniz bilgilerinizi yönetmek için{' '}
                      <a href={`/isortagi-basvuru/${venue.slug}`} className="font-semibold underline hover:text-amber-900">
                        sahiplenme başvurusu
                      </a>
                      {' '}yapabilirsiniz. Kaldırma talepleriniz için iletişim bölümünden bize ulaşabilirsiniz.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <p className="text-base text-gray-600 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-pink-600" />
              {venue.cityName} / {venue.districtName} Davet Evi
            </p>
            {venue.totalViews > 0 && (
              <p className="text-sm text-gray-500 flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-gray-400" />
                <span>{venue.totalViews.toLocaleString('tr-TR')} kişi görüntüledi</span>
              </p>
            )}
          </div>
        </div>

        {/* Rating Badge */}
        {venue.rating && venue.rating > 0 && !venue.hideGoogleReviews && (
          <div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-50 to-orange-50 px-4 py-3 rounded-2xl border border-yellow-200 mb-6 cursor-pointer hover:shadow-md hover:border-yellow-300 transition-all duration-300"
            onClick={() => {
              const reviewsSection = document.getElementById('reviews-section');
              if (reviewsSection) reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            title="Yorumları görmek için tıklayın"
          >
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="font-bold text-xl text-gray-900">{venue.rating.toFixed(1)}</span>
            </div>
            {venue.reviewCount > 0 && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600 font-medium">{venue.reviewCount} değerlendirme</span>
              </>
            )}
          </div>
        )}

        {/* Verified Badge */}
        {isVenueClaimed(venue) && (
          <div className="flex items-start gap-3 p-4 rounded-xl mb-6 bg-green-50 border border-green-200">
            <div className="p-2 rounded-lg bg-green-100">
              <BadgeCheck className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-sm leading-relaxed text-green-700">
              Bilgiler işletme sahibi tarafından doğrulanmakta ve güncellenmektedir.
            </p>
          </div>
        )}

        {/* Description */}
        {isVenueClaimed(venue) && (
          <p className="text-base text-gray-700 mb-6 leading-relaxed">
            {venue.cityName} {venue.districtName} bölgesinde {venue.capacity?.min && venue.capacity?.max ? `${venue.capacity.min}-${venue.capacity.max} kişilik ` : ''}profesyonel davet evi hizmeti sunuyoruz.
          </p>
        )}

        {/* Share Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <span className="text-sm font-medium text-gray-700">Paylaş:</span>
          <div className="flex gap-2">
            <button
              onClick={() => onShare('whatsapp')}
              className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl transition-all duration-200 hover:scale-105"
              title="WhatsApp'ta Paylaş"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">WhatsApp</span>
            </button>
            <button
              onClick={() => onShare('copy')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all duration-200 hover:scale-105"
              title="Linki Kopyala"
            >
              <Copy className="w-4 h-4" />
              <span className="text-sm font-medium">Kopyala</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

VenueInfoCard.displayName = 'VenueInfoCard';
export default VenueInfoCard;
